import React, { useEffect, useMemo, useRef, useState } from 'react';

import { useSelector } from 'react-redux';

import BaseSelector from '@src/components/BaseSelector';
import type { CostCenter } from '@src/models/interfaces/costCenter.interface';
import CostCenterService from '@src/services/CostCenterService';

import { flattenActiveForSelector } from '../../utils/buildCostCenterTree';

interface CostCenterSelectorProps {
  value?: string | null;
  onChange?: (value: string | null) => void;
  disabled?: boolean;
  /** When false, hide the synthetic “all / clear” option (forms). */
  allowClear?: boolean;
  className?: string;
  placeholderId?: string;
}

type SelectorItem = CostCenter & { label: string };

/**
 * Active cost centers only, labelled as `client › branch` when nested.
 */
const CostCenterSelector: React.FC<CostCenterSelectorProps> = ({
  value,
  onChange,
  disabled,
  allowClear = true,
  className,
  placeholderId = 'costCenters.selector.placeholder',
}) => {
  const organizationId = useSelector(
    ({ organizations }: { organizations: { organization: { id?: string } } }) =>
      organizations.organization?.id,
  );
  const serviceRef = useRef<CostCenterService | null>(null);
  if (serviceRef.current === null) {
    serviceRef.current = new CostCenterService();
  }
  const [items, setItems] = useState<SelectorItem[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!organizationId) {
      setItems([]);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    serviceRef.current!
      .list(organizationId, true)
      .then(list => {
        if (!cancelled) setItems(flattenActiveForSelector(list));
      })
      .catch(() => {
        if (!cancelled) setItems([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [organizationId]);

  const handleChange = useMemo(
    () => (next: string | undefined) => {
      onChange?.(next || null);
    },
    [onChange],
  );

  return (
    <BaseSelector<SelectorItem>
      items={items}
      loading={loading}
      value={value ?? undefined}
      onChange={handleChange}
      disabled={disabled}
      className={className}
      placeholderId={placeholderId}
      showAllOption={false}
      mode={undefined}
      getItemId={item => item.id}
      getItemName={item => item.label}
      // BaseSelector forwards unknown props to antd Select.
      {...({ allowClear } as Record<string, unknown>)}
    />
  );
};

export default React.memo(CostCenterSelector);
